import { Badge } from '@/platform/ui/badge';
import { Shield, User, Crown } from 'lucide-react';
import { RoleType } from '@/lib/db/types';

interface RoleBadgeProps {
  role: RoleType;
  showIcon?: boolean;
  variant?: 'default' | 'secondary' | 'outline' | 'destructive';
}

const roleConfig: Record<RoleType, { label: string; icon: any; className: string }> = {
  superadmin: {
    label: 'Super Admin',
    icon: Crown,
    className: 'bg-pillar-agility-bg text-pillar-agility-text border-transparent',
  },
  admin: {
    label: 'Admin',
    icon: Shield,
    className: 'bg-pillar-conscious-comm-bg text-pillar-conscious-comm-text border-transparent',
  },
  owner: {
    label: 'Owner',
    icon: Crown,
    className: 'bg-warning/10 text-warning border-warning/20',
  },
  member: {
    label: 'Member',
    icon: User,
    className: 'bg-secondary text-secondary-foreground border-transparent',
  },
};

export function RoleBadge({ role, showIcon = true, variant }: RoleBadgeProps) {
  const config = roleConfig[role];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <Badge
      variant={variant}
      className={variant ? undefined : config.className}
    >
      {showIcon && <Icon className="h-3 w-3 mr-1" />}
      {config.label}
    </Badge>
  );
}

export function getRoleDisplayName(role: RoleType): string {
  return roleConfig[role]?.label || role;
}

export function getRoleDescription(role: RoleType): string {
  const descriptions: Record<RoleType, string> = {
    superadmin: 'Full system access with all administrative privileges',
    admin: 'Administrative access to manage users and content',
    owner: 'Organization owner with full administrative access',
    member: 'Organization member with standard access',
  };
  return descriptions[role] || 'User role';
}
